/**
 * Scheduled tasks management page
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, RefreshCw, Clock, Calendar, Edit } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { api } from '../lib/api'
import { formatTimestamp } from '../lib/utils'

export function ScheduledTasks() {

  // Get workflows with scheduling enabled
  const { data: workflowsData, isLoading, error, refetch } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api.getWorkflows(),
  })

  // Filter workflows that have scheduling enabled (have cron expression)
  const scheduledWorkflows = workflowsData?.workflows?.filter(w => w.cron) || []

  const getScheduleDescription = (cron: string) => {
    // Simple cron description - could be enhanced with a proper cron parser
    const parts = cron.split(' ')
    if (parts.length === 5) {
      const [minute, hour, day, , dayOfWeek] = parts

      if (dayOfWeek !== '*') {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        const dayNames = dayOfWeek.split(',').map(d => days[parseInt(d)]).join(', ')
        return `Every ${dayNames} at ${hour}:${minute.padStart(2, '0')}`
      } else if (day !== '*') {
        return `Monthly on day ${day} at ${hour}:${minute.padStart(2, '0')}`
      } else {
        return `Daily at ${hour}:${minute.padStart(2, '0')}`
      }
    }
    return cron
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Scheduled Workflows</h1>
          <p className="text-muted-foreground">
            Workflows that run automatically on a schedule
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Link to="/workflows/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Workflow
            </Button>
          </Link>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load scheduled workflows. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      {/* Scheduled Workflows */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Scheduled Workflows ({scheduledWorkflows.length})
          </CardTitle>
          <CardDescription>
            Workflows with automatic scheduling enabled
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : scheduledWorkflows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workflow</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scheduledWorkflows.map((workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{workflow.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {workflow.description || 'No description'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          {getScheduleDescription(workflow.cron || '')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {workflow.timezone} • {workflow.cron}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={workflow.status === 'running' ? 'default' : 'secondary'}>
                        {workflow.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatTimestamp(new Date(workflow.updated_at).getTime())}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link to={`/workflows/${workflow.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={`/workflows/${workflow.id}`}>
                          <Button variant="ghost" size="sm">
                            <Calendar className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Scheduled Workflows</h3>
              <p className="text-muted-foreground mb-4">
                Create workflows and enable scheduling to see them here.
              </p>
              <Link to="/workflows/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Workflow
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
